// src/pages/LandingPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import Header from '../components/Header'; // IMPORTAMOS EL NUEVO HEADER

// Importación correcta de imágenes
import LogoAlgorithmia from '../../src/assets/LOGOAZUL-Ajpg.jpg';
import LogoAlgorithmiaInicio from '../../src/assets/logo inicial.jpeg';

// Importación de iconos Heroicons
import { 
  CheckCircleIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  CursorArrowRaysIcon,
  CloudIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
  IdentificationIcon,
  KeyIcon,
  DocumentMagnifyingGlassIcon,
  SignalIcon,
  DocumentTextIcon,
  BellAlertIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  SparklesIcon,
  BuildingStorefrontIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  ChartPieIcon,
  MegaphoneIcon,
  DocumentChartBarIcon,
  WrenchScrewdriverIcon,
  AcademicCapIcon,
  ExclamationCircleIcon,
  BuildingOfficeIcon,
  CpuChipIcon,
  CodeBracketIcon,
  BeakerIcon,
  ServerIcon,
  CloudArrowUpIcon,
  DeviceTabletIcon,
  FingerPrintIcon,
  LockClosedIcon,
  EyeIcon,
  ClockIcon,
  CheckBadgeIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

// Componente Iridescence mejorado
import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';
import { useEffect as useReactEffect, useRef as useReactRef } from 'react';

// ================================================
// IMPORTACIONES PARA SEO Y RENDIMIENTO
// ================================================
import { keywordStrategy, serviceKeywords } from '../data/keywords';

// ================================================
// INTERFACES NUEVAS
// ================================================

interface KineticImage {
  id: number;
  src: string;
  title: string;
  description: string;
  category: string;
  color: string;
  parallaxSpeed: number;
  seoKeywords?: string[];
}

interface KineticGalleryProps {
  images: KineticImage[];
  className?: string;
}

// ================================================
// COMPONENTE PRINCIPAL: KINETIC GALLERY
// ================================================

const KineticGallery: React.FC<KineticGalleryProps> = ({ images, className = "" }) => {
  const galleryRef = useRef<HTMLDivElement>(null);
  const [activeImage, setActiveImage] = useState<number>(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (galleryRef.current) {
        const rect = galleryRef.current.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, 
          (window.innerHeight - rect.top) / (rect.height + window.innerHeight)
        ));
        setScrollProgress(progress);
        
        const imageIndex = Math.floor(progress * images.length);
        setActiveImage(Math.min(imageIndex, images.length - 1));
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [images.length]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (galleryRef.current) {
        const rect = galleryRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      ref={galleryRef} 
      className={`relative min-h-screen ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <motion.div 
        className="absolute inset-0 z-0"
        style={{
          background: `linear-gradient(135deg, ${images[activeImage]?.color}20 0%, #0E2544 100%)`
        }}
        animate={{
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />

      <div className="absolute inset-0 z-1">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              background: images[activeImage]?.color || '#4ADE80',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, mousePosition.x * 20, 0],
              y: [0, mousePosition.y * 20, 0],
              opacity: [0, 0.7 * Math.random(), 0],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              repeatType: 'reverse',
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-32">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-block">
            <div className="w-32 h-1 bg-gradient-to-r from-[#4ADE80] via-white to-[#3BC571] rounded-full mx-auto mb-6"></div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <LiquidText>Nuestro Trabajo en Acción</LiquidText>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Experiencias visuales que transforman ideas en realidades digitales impactantes
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {images.map((image, index) => (
            <ImageCard 
              key={image.id}
              image={image}
              index={index}
              isActive={index === activeImage}
              mousePosition={mousePosition}
              scrollProgress={scrollProgress}
              isHovering={isHovering}
            />
          ))}
        </div>

        <ActiveImagePanel 
          image={images[activeImage]}
          activeIndex={activeImage}
          totalImages={images.length}
        />
      </div>

      <TransitionEffects 
        activeImage={activeImage}
        previousImage={Math.max(0, activeImage - 1)}
        images={images}
      />
    </div>
  );
};

// ================================================
// COMPONENTE TARJETA DE IMAGEN
// ================================================

interface ImageCardProps {
  image: KineticImage;
  index: number;
  isActive: boolean;
  mousePosition: { x: number; y: number };
  scrollProgress: number;
  isHovering: boolean;
}

const ImageCard: React.FC<ImageCardProps> = ({
  image,
  index,
  isActive,
  mousePosition,
  scrollProgress,
  isHovering
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [localMousePosition, setLocalMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      setLocalMousePosition({ x, y });
    }
  };

  const rotationX = localMousePosition.y * 10;
  const rotationY = localMousePosition.x * 10;
  const translateX = localMousePosition.x * 20;
  const translateY = localMousePosition.y * 20;
  const scale = isHovering ? 1.05 : 1;
  const opacity = isActive ? 1 : 0.7;
  const blur = isActive ? 'blur(0px)' : 'blur(2px)';

  return (
    <motion.div
      ref={cardRef}
      className="relative group cursor-pointer"
      onMouseMove={handleMouseMove}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{
        perspective: '1000px'
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at ${localMousePosition.x * 50 + 50}% ${localMousePosition.y * 50 + 50}%, ${image.color}40 0%, transparent 70%)`,
          filter: 'blur(20px)'
        }}
        animate={{
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />

      <motion.div
        className="relative rounded-3xl overflow-hidden border border-white/20 bg-white/5 backdrop-blur-sm"
        style={{
          transform: `
            perspective(1000px)
            rotateX(${rotationX}deg)
            rotateY(${rotationY}deg)
            translateX(${translateX}px)
            translateY(${translateY}px)
            scale(${scale})
          `,
          transformStyle: 'preserve-3d',
          filter: blur,
          opacity: opacity
        }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 20
        }}
      >
        <motion.div
          className="relative h-96 w-full"
          style={{
            backgroundImage: `url(${image.src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateZ(${scrollProgress * 50}px)`
          }}
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${Math.random() * 3 + 1}px`,
                  height: `${Math.random() * 3 + 1}px`,
                  background: image.color,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  x: [0, (Math.random() - 0.5) * 10, 0],
                  opacity: [0, 0.8, 0],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: Math.random() * 3 + 2,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  delay: Math.random() * 2
                }}
              />
            ))}
          </div>
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <motion.h3 
            className="text-2xl font-bold mb-2"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            {image.title}
          </motion.h3>
          <motion.p 
            className="text-white/80"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 + 0.1 }}
          >
            {image.description}
          </motion.p>
          
          <motion.div
            className="inline-block mt-3 px-4 py-1.5 rounded-full text-sm font-medium"
            style={{ background: `${image.color}40` }}
            whileHover={{ scale: 1.1 }}
          >
            {image.category}
          </motion.div>
        </div>

        <motion.div
          className="absolute inset-0 rounded-3xl border-2 pointer-events-none"
          style={{ borderColor: image.color }}
          animate={{
            boxShadow: [
              `0 0 10px ${image.color}20`,
              `0 0 30px ${image.color}40`,
              `0 0 10px ${image.color}20`
            ]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
        />
      </motion.div>
    </motion.div>
  );
};

// ================================================
// COMPONENTE PANEL DE IMAGEN ACTIVA
// ================================================

interface ActiveImagePanelProps {
  image: KineticImage;
  activeIndex: number;
  totalImages: number;
}

const ActiveImagePanel: React.FC<ActiveImagePanelProps> = ({ 
  image, 
  activeIndex, 
  totalImages 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      viewport={{ once: true }}
      className="mt-20 bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 relative overflow-hidden"
    >
      <motion.div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(45deg, ${image.color}10, transparent)`,
        }}
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse',
          ease: "linear"
        }}
      />

      <div className="relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          <div className="lg:w-2/3">
            <div className="flex items-center gap-4 mb-6">
              <div className="text-5xl font-bold text-white">
                {String(activeIndex + 1).padStart(2, '0')}
              </div>
              <div className="h-0.5 flex-1 bg-gradient-to-r from-white/50 to-transparent"></div>
              <div className="text-white/50">
                {String(totalImages).padStart(2, '0')}
              </div>
            </div>
            
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {image.title}
            </h3>
            
            <p className="text-xl text-white/80 mb-6">
              {image.description}
            </p>

            <div className="flex items-center gap-4">
              {[...Array(totalImages)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === activeIndex 
                      ? 'bg-white w-12' 
                      : i < activeIndex 
                        ? 'bg-white/50 w-8' 
                        : 'bg-white/20 w-4'
                  }`}
                  whileHover={{ scale: 1.2 }}
                />
              ))}
            </div>
          </div>

          <div className="lg:w-1/3">
            <div className="bg-black/30 rounded-2xl p-6 border border-white/10">
              <h4 className="text-lg font-semibold text-white mb-4">
                Detalles del Proyecto
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Categoría</span>
                  <span className="font-medium text-white">{image.category}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Impacto Visual</span>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-4 rounded-full"
                        style={{ background: image.color }}
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: i * 0.1
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Nivel de Interacción</span>
                  <motion.div
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ background: `${image.color}40` }}
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: 'reverse'
                    }}
                  >
                    Alta
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ================================================
// COMPONENTE EFECTOS DE TRANSICIÓN
// ================================================

interface TransitionEffectsProps {
  activeImage: number;
  previousImage: number;
  images: KineticImage[];
}

const TransitionEffects: React.FC<TransitionEffectsProps> = ({
  activeImage,
  previousImage,
  images
}) => {
  return (
    <>
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0, 0.3, 0],
          scale: [1, 1.5, 2]
        }}
        transition={{
          duration: 1,
          times: [0, 0.5, 1]
        }}
        key={activeImage}
        style={{
          background: `radial-gradient(circle, ${images[activeImage]?.color}30 0%, transparent 70%)`,
        }}
      />

      {[...Array(30)].map((_, i) => (
        <motion.div
          key={`transition-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: `${Math.random() * 4 + 2}px`,
            height: `${Math.random() * 4 + 2}px`,
            background: images[activeImage]?.color || '#4ADE80',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }}
          animate={{
            x: [(Math.random() - 0.5) * 200, (Math.random() - 0.5) * 400],
            y: [(Math.random() - 0.5) * 200, (Math.random() - 0.5) * 400],
            opacity: [0, 1, 0],
            scale: [0, 1, 0]
          }}
          transition={{
            duration: 1.5,
            times: [0, 0.5, 1],
            delay: Math.random() * 0.5
          }}
        />
      ))}
    </>
  );
};

// ================================================
// IMÁGENES DE GALERÍA CON KEYWORDS OPTIMIZADAS
// ================================================

const galleryImages: KineticImage[] = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    title: "Dashboard Inteligente - Business Intelligence",
    description: "Panel de control con visualización de datos en tiempo real y análisis predictivo para empresas",
    category: "Business Intelligence",
    color: "#4ADE80",
    parallaxSpeed: 0.3,
    seoKeywords: ["dashboard inteligente", "business intelligence argentina", "visualización datos", "análisis predictivo"]
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    title: "Sistema de Gestión ERP con IA",
    description: "Plataforma integral para administración empresarial con inteligencia artificial integrada",
    category: "ERP",
    color: "#3BC571",
    parallaxSpeed: 0.4,
    seoKeywords: ["sistema gestión empresarial", "erp argentina", "ia negocios", "administración empresas"]
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    title: "Analítica Avanzada y Machine Learning",
    description: "Herramientas de análisis de datos con machine learning y reportes automáticos",
    category: "Data Science",
    color: "#2D4A7D",
    parallaxSpeed: 0.5,
    seoKeywords: ["analítica avanzada", "machine learning argentina", "ciencia datos", "reportes automáticos"]
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    title: "App Móvil PWA - Funcionamiento Offline",
    description: "Aplicación progresiva multiplataforma con funcionamiento offline y sincronización automática",
    category: "Mobile",
    color: "#0E2544",
    parallaxSpeed: 0.6,
    seoKeywords: ["app pwa argentina", "aplicación offline", "desarrollo móvil", "progressive web app"]
  },
  {
    id: 5,
    src: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    title: "Integración API en Tiempo Real",
    description: "Sistema de conexión entre plataformas con sincronización en tiempo real y microservicios",
    category: "DevOps",
    color: "#4ADE80",
    parallaxSpeed: 0.7,
    seoKeywords: ["integración api", "microservicios", "devops argentina", "sincronización tiempo real"]
  },
  {
    id: 6,
    src: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    title: "Seguridad Blockchain y Encriptación",
    description: "Protección de datos con tecnología distribuida y encriptación avanzada",
    category: "Security",
    color: "#3BC571",
    parallaxSpeed: 0.8,
    seoKeywords: ["seguridad blockchain", "encriptación datos", "ciberseguridad argentina", "protección información"]
  }
];

// ================================================
// SHADERS PARA IRIDESCENCE
// ================================================

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  d += uTime * 0.5 * uSpeed;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, 1.0);
}
`;

interface IridescenceProps {
  color?: [number, number, number];
  speed?: number;
  amplitude?: number;
  mouseReact?: boolean;
  className?: string;
}

function Iridescence({
  color = [0.29, 0.87, 0.51],
  speed = 1.0,
  amplitude = 0.1,
  mouseReact = true,
  className = '',
  ...rest
}: IridescenceProps) {
  const ctnDom = useReactRef<HTMLDivElement>(null);
  const mousePos = useReactRef({ x: 0.5, y: 0.5 });
  const [isMobile, setIsMobile] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  useReactEffect(() => {
    const checkDeviceAndZoom = () => {
      setIsMobile(window.innerWidth < 768);
      const zoom = window.outerWidth / window.innerWidth;
      setZoomLevel(zoom);
    };

    checkDeviceAndZoom();
    window.addEventListener('resize', checkDeviceAndZoom);
    
    return () => {
      window.removeEventListener('resize', checkDeviceAndZoom);
    };
  }, []);

  useReactEffect(() => {
    if (!ctnDom.current) return;
    
    const ctn = ctnDom.current;
    const renderer = new Renderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    
    const gl = renderer.gl;
    gl.clearColor(0.0, 0.0, 0.0, 0.0);

    let program: Program;
    let isResizing = false;

    function resize() {
      if (isResizing) return;
      isResizing = true;
      
      requestAnimationFrame(() => {
        const width = ctn.offsetWidth;
        const height = ctn.offsetHeight;
        
        const pixelRatio = (window.devicePixelRatio || 1) * zoomLevel;
        const realWidth = width * pixelRatio;
        const realHeight = height * pixelRatio;
        
        renderer.setSize(realWidth, realHeight);
        
        gl.canvas.style.width = `${width}px`;
        gl.canvas.style.height = `${height}px`;
        
        if (program) {
          program.uniforms.uResolution.value = new Color(
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height
          );
        }
        
        isResizing = false;
      });
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(ctn);
    
    window.addEventListener('resize', resize);
    resize();

    const geometry = new Triangle(gl);
    program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(...color) },
        uResolution: {
          value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height)
        },
        uMouse: { value: new Float32Array([mousePos.current.x, mousePos.current.y]) },
        uAmplitude: { value: amplitude * (isMobile ? 0.5 : 1) },
        uSpeed: { value: speed * (isMobile ? 0.8 : 1) }
      }
    });

    const mesh = new Mesh(gl, { geometry, program });
    let animateId: number;

    function update(t: number) {
      animateId = requestAnimationFrame(update);
      program.uniforms.uTime.value = t * 0.001;
      renderer.render({ scene: mesh });
    }
    animateId = requestAnimationFrame(update);
    
    gl.canvas.style.position = 'absolute';
    gl.canvas.style.top = '0';
    gl.canvas.style.left = '0';
    gl.canvas.style.width = '100%';
    gl.canvas.style.height = '100%';
    gl.canvas.style.pointerEvents = 'none';
    ctn.appendChild(gl.canvas);

    function handleMouseMove(e: MouseEvent) {
      if (!mouseReact || isMobile) return;
      
      const rect = ctn.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      mousePos.current = { x, y };
      program.uniforms.uMouse.value[0] = x;
      program.uniforms.uMouse.value[1] = y;
    }

    if (mouseReact && !isMobile) {
      ctn.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      cancelAnimationFrame(animateId);
      window.removeEventListener('resize', resize);
      resizeObserver.disconnect();
      
      if (mouseReact && !isMobile) {
        ctn.removeEventListener('mousemove', handleMouseMove);
      }
      
      if (ctn.contains(gl.canvas)) {
        ctn.removeChild(gl.canvas);
      }
      
      try {
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      } catch (e) {
        console.log('WebGL context cleanup');
      }
    };
  }, [color, speed, amplitude, mouseReact, isMobile, zoomLevel]);

  return (
    <div 
      ref={ctnDom} 
      className={`w-full h-full fixed inset-0 ${className}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: mouseReact ? 'auto' : 'none',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
      {...rest}
    />
  );
}

// ================================================
// COMPONENTE SPLASH SCREEN
// ================================================

const SplashScreen = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const duration = 3000;
    const interval = 30;
    const totalSteps = duration / interval;
    const increment = 100 / totalSteps;
    
    const timer = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(timer);
          return 100;
        }
        return next;
      });
    }, interval);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] flex flex-col items-center justify-center"
    >
      <motion.div 
        className="relative mb-8 sm:mb-12"
        initial={{ scale: 0.8, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 1, type: "spring" }}
      >
        <div className="relative h-48 w-48 sm:h-56 sm:w-56 md:h-64 md:w-64 lg:h-72 lg:w-72 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-3xl flex items-center justify-center shadow-2xl">
          <OptimizedImage 
            src={LogoAlgorithmiaInicio} 
            alt="Algorithmia - Desarrollo de Software a Medida en Córdoba, Argentina" 
            className="w-4/5 h-4/5 object-contain p-2"
          />
          
          <motion.div 
            className="absolute inset-0 rounded-3xl border-2 border-white/30"
            animate={{ 
              boxShadow: [
                "0 0 30px rgba(74, 222, 128, 0.5)",
                "0 0 60px rgba(74, 222, 128, 0.8)",
                "0 0 30px rgba(74, 222, 128, 0.5)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          <div className="absolute inset-0">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-white/30"
                style={{
                  width: '8px',
                  height: '8px',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{
                  scale: [0, 1.5, 0],
                  opacity: [0, 1, 0],
                  x: [
                    0,
                    Math.cos((i / 8) * Math.PI * 2) * 140,
                    Math.cos((i / 8) * Math.PI * 2) * 160
                  ],
                  y: [
                    0,
                    Math.sin((i / 8) * Math.PI * 2) * 140,
                    Math.sin((i / 8) * Math.PI * 2) * 160
                  ]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: "easeOut"
                }}
              />
            ))}
          </div>
        </div>
      </motion.div>
      
      <div className="relative mb-6">
        <motion.div
          key={Math.round(progress)}
          initial={{ scale: 0.9, opacity: 0.7 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2, type: "spring" }}
          className="text-center"
        >
          <span className="text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight drop-shadow-lg">
            {Math.round(progress)}%
          </span>
        </motion.div>
        
        <div className="w-48 sm:w-56 md:w-64 h-2 bg-white/20 rounded-full overflow-hidden mt-6">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#4ADE80] to-[#3BC571]"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
        
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-center mt-4"
        >
          <p className="text-white/90 text-base sm:text-lg font-light">
            Cargando tu experiencia
          </p>
        </motion.div>
      </div>
      
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: `${Math.random() * 4 + 2}px`,
            height: `${Math.random() * 4 + 2}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: '#4ADE80'
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, (Math.random() - 0.5) * 15, 0],
            opacity: [0, 0.6, 0],
            scale: [1, 1.8, 1]
          }}
          transition={{
            duration: Math.random() * 3 + 3,
            repeat: Infinity,
            repeatType: 'reverse',
            delay: Math.random() * 2
          }}
        />
      ))}
      
      <div className="absolute bottom-6">
        <p className="text-white/20 text-xs">
          v{process.env.REACT_APP_VERSION || '1.0.0'}
        </p>
      </div>
    </motion.div>
  );
};

// ================================================
// COMPONENTES DE EFECTOS VISUALES
// ================================================

interface ParticleSystemProps {}
interface LiquidTextProps {
  children: React.ReactNode;
  className?: string;
}
interface MorphingSectionProps {
  children: React.ReactNode;
  id?: string;
  className?: string;
}
interface ParallaxSectionProps {
  children: React.ReactNode;
  speed?: number;
  className?: string;
}

const ParticleSystem: React.FC<ParticleSystemProps> = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div ref={containerRef} className="fixed inset-0 pointer-events-none z-0">
      {[...Array(60)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${Math.random() * 4 + 1}px`,
            height: `${Math.random() * 4 + 1}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: i % 4 === 0 ? '#4ADE80' : 
                       i % 4 === 1 ? '#3BC571' : 
                       i % 4 === 2 ? '#2D4A7D' : '#FFFFFF',
          }}
          animate={{
            x: [0, (Math.random() - 0.5) * 40 + mousePosition.x * 0.1],
            y: [0, (Math.random() - 0.5) * 40 + mousePosition.y * 0.1],
            opacity: [0, 0.8 * Math.random(), 0],
            scale: [1, 1.5, 1]
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: "easeInOut",
            delay: Math.random() * 5
          }}
        />
      ))}
    </div>
  );
};

const LiquidText: React.FC<LiquidTextProps> = ({ children, className = "" }) => {
  return (
    <motion.span
      className={`relative inline-block pb-1 ${className}`}
      animate={{
        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      }}
      transition={{
        duration: 5,
        repeat: Infinity,
        ease: "linear"
      }}
      style={{
        background: "linear-gradient(90deg, #4ADE80, #3BC571, #2D4A7D, #4ADE80)",
        backgroundSize: "300% 300%",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        lineHeight: "1.1",
      }}
    >
      {children}
      <span className="absolute opacity-0" aria-hidden="true">g</span>
    </motion.span>
  );
};

const MorphingSection: React.FC<MorphingSectionProps> = ({ 
  children, 
  id, 
  className = "" 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <motion.section
      ref={sectionRef}
      id={id}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className={`relative ${className}`}
    >
      <motion.div
        className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-r from-[#4ADE80]/10 to-[#3BC571]/10 rounded-full blur-3xl"
        animate={isVisible ? {
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
          borderRadius: ["50%", "30%", "50%"]
        } : {}}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />
      
      {children}
    </motion.section>
  );
};

const ParallaxSection: React.FC<ParallaxSectionProps> = ({ 
  children, 
  speed = 0.5, 
  className = "" 
}) => {
  const [offset, setOffset] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const scrollPercent = (rect.top / window.innerHeight) * 100;
        setOffset(scrollPercent * speed);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <motion.div 
        style={{ y: offset }}
        transition={{ type: "spring", stiffness: 100 }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// ================================================
// HOOK PERSONALIZADO PARA SCROLL ANIMATION
// ================================================

const useScrollAnimation = () => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollY(window.scrollY);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { scrollY, scrollProgress };
};

// ================================================
// COMPONENTE PRINCIPAL: ABOUTPAGE
// ================================================

const AboutPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  
  const { scrollY, scrollProgress } = useScrollAnimation();

  // ================================================
  // VERIFICACIÓN DE GOOGLE SEARCH CONSOLE - META TAG
  // ================================================
  useEffect(() => {
    const verifySearchConsole = () => {
      const metaTag = document.createElement('meta');
      metaTag.name = 'google-site-verification';
      metaTag.content = 'IdSs5aN6W5ezNK6_clc0hX3SI31sAcYGD5aQ_mvRvv0';
      document.head.appendChild(metaTag);
    };

    verifySearchConsole();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3200);

    if (document.readyState === 'complete') {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      clearTimeout(timer);
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => {
          setIsLoading(false);
        }, 3000);
        clearTimeout(timer);
      });
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', () => {});
    };
  }, []);

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const featureImages: string[] = [];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === featureImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [featureImages.length]);

  // ================================================
  // DATOS DE LA EMPRESA OPTIMIZADOS PARA SEO
  // ================================================

  const values = [
    { icon: ShieldCheckIcon, title: "Confianza", description: "Soluciones robustas y seguras para tu negocio" },
    { icon: LightBulbIcon, title: "Innovación", description: "Tecnologías modernas y metodologías ágiles" },
    { icon: CursorArrowRaysIcon, title: "Simplicidad", description: "Interfaces intuitivas y fácil adopción" },
    { icon: CloudIcon, title: "Escalabilidad", description: "Crecimiento sostenido con tu empresa" },
    { icon: UsersIcon, title: "Colaboración", description: "Trabajamos como equipo contigo" }
  ];

  const strategicObjectives = [
    "Digitalizar procesos críticos de gestión empresarial",
    "Reducir tiempos operativos mediante automatización inteligente",
    "Garantizar cumplimiento normativo en Higiene y Seguridad",
    "Facilitar acceso a información en tiempo real, incluso offline",
    "Optimizar recursos mediante análisis de datos y reporting"
  ];

  // ================================================
  // SERVICIOS CON KEYWORDS OPTIMIZADAS
  // ================================================

  const services = [
    { 
      icon: ClipboardDocumentCheckIcon, 
      title: "Sistema de Inspecciones PWA",
      description: "Aplicativo web progresivo para inspecciones de mantenimiento, seguridad industrial y medio ambiente",
      features: ["Checklists personalizables", "Modo offline", "Captura de evidencias", "Reportes automáticos"],
      keywords: serviceKeywords.sistemaInspecciones
    },
    { 
      icon: ExclamationTriangleIcon, 
      title: "Gestión de Riesgos Laborales",
      description: "Identificación de peligros y evaluación de riesgos en puestos de trabajo",
      features: ["Matrices de riesgo", "Análisis por área", "Plan de acción", "Seguimiento continuo"],
      keywords: serviceKeywords.gestionRiesgos
    },
    { 
      icon: Squares2X2Icon, 
      title: "Órdenes de Trabajo con Kanban",
      description: "Gestión de tareas con tablero Kanban y notificaciones automáticas",
      features: ["Kanban visual", "Notificaciones Telegram", "Seguimiento 7-3-1 días", "Asignación dinámica"],
      keywords: serviceKeywords.ordenesTrabajo
    },
    { 
      icon: MapPinIcon, 
      title: "Registro de Visitas con Geolocalización",
      description: "Control de visitas con geolocalización, fotos y dictado por voz",
      features: ["Geolocalización", "Multimedia integrado", "Voz a texto", "Reportes PDF"],
      keywords: { keywords: ["control visitas geolocalización", "app registro visitas", "gestión visitas empresas"] }
    },
    { 
      icon: DocumentMagnifyingGlassIcon, 
      title: "Investigación de Accidentes",
      description: "Análisis con método de espina de pescado y 5 porqués",
      features: ["Diagramas Ishikawa", "Plan de acciones", "Asignación responsables", "Seguimiento medidas"],
      keywords: { keywords: ["investigación accidentes laborales", "software análisis causas", "método ishikawa"] }
    },
    { 
      icon: UserGroupIcon, 
      title: "Gestión de RRHH",
      description: "Administración de empleados y capacitaciones con búsqueda por DNI",
      features: ["Registro completo", "Gestión capacitaciones", "Búsqueda ágil", "Historial digital"],
      keywords: serviceKeywords.rrhh
    },
    { 
      icon: DocumentTextIcon, 
      title: "Firma Digital",
      description: "Sistema de firma digital con trazabilidad completa",
      features: ["Geolocalización", "Fecha/hora certificada", "Responsable identificado", "Archivo seguro"],
      keywords: serviceKeywords.firmaDigital
    },
    { 
      icon: AcademicCapIcon, 
      title: "Estudios Ergonómicos",
      description: "Protocolos según normativa argentina con anexos completos",
      features: ["Protocolos oficiales", "Cálculos automáticos", "Recomendaciones", "Reportes normativos"],
      keywords: serviceKeywords.estudiosErgonomicos
    },
    { 
      icon: ChartBarIcon, 
      title: "Mediciones Ambientales",
      description: "Registro de ruido e iluminación según resolución legal",
      features: ["Cumplimiento legal", "Registros históricos", "Alertas límites", "Reportes oficiales"],
      keywords: serviceKeywords.medicionesAmbientales
    },
    { 
      icon: BellAlertIcon, 
      title: "Avisos de Riesgos",
      description: "Gestión de alertas por criticidad y responsables",
      features: ["Priorización automática", "Asignación áreas", "Seguimiento", "Cierre validado"],
      keywords: { keywords: ["alertas riesgos laborales", "gestión avisos seguridad", "notificaciones incidentes"] }
    },
    { 
      icon: ScaleIcon,
      title: "Control de Pesaje",
      description: "Registro de kilos en cargas/descargas con PDF detallado",
      features: ["Cálculo automático", "PDF personalizado", "Histórico completo", "Integración silos"],
      keywords: serviceKeywords.controlPesaje
    }
  ];

  const whyChooseUs = [
    "Desarrollo 100% personalizado",
    "Tecnología PWA (Progressive Web App)",
    "Funcionalidad offline completa",
    "Integraciones con sistemas existentes",
    "Soporte técnico especializado",
    "Actualizaciones continuas",
    "Cumplimiento normativo garantizado",
    "Escalabilidad asegurada"
  ];

  const technologies = [
    { name: "React", color: "from-[#4ADE80] to-[#3BC571]" },
    { name: "TypeScript", color: "from-[#0E2544] to-[#2D4A7D]" },
    { name: "Node.js", color: "from-[#4ADE80] to-[#3BC571]" },
    { name: "PWA", color: "from-[#0E2544] to-[#4ADE80]" },
    { name: "MongoDB", color: "from-[#4ADE80] to-[#3BC571]" },
    { name: "AWS", color: "from-[#0E2544] to-[#4ADE80]" },
    { name: "Docker", color: "from-[#4ADE80] to-[#3BC571]" },
    { name: "Git", color: "from-[#0E2544] to-[#2D4A7D]" }
  ];

  const workProcess = [
    { step: "1", title: "Análisis", description: "Entendemos tus necesidades y desafíos" },
    { step: "2", title: "Diseño", description: "Creamos la arquitectura y UX/UI óptima" },
    { step: "3", title: "Desarrollo", description: "Implementamos con metodologías ágiles" },
    { step: "4", title: "Testing", description: "Garantizamos calidad y seguridad" },
    { step: "5", title: "Implementación", description: "Despliegue y capacitación" },
    { step: "6", title: "Soporte", description: "Mantenimiento y mejoras continuas" }
  ];

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <>
      {/* ================================================ */}
      {/* SEO META TAGS COMPLETOS */}
      {/* ================================================ */}
      <SEO 
        title="Algorithmia - Desarrollo de Software a Medida"
        description="Expertos en desarrollo de software personalizado. ¡Solicita tu demo gratis!"
        keywords={keywordStrategy.primary.map(k => k.keyword).join(', ') + ', ' + keywordStrategy.secondary.join(', ') + ', ' + keywordStrategy.longTail.join(', ')}
        image="https://algorithmia.com/og-image.jpg"
        url="https://algorithmia.com"
        type="website"
        publishedTime="2024-01-01"
        modifiedTime={new Date().toISOString().split('T')[0]}
        author="Algorithmia Team"
        section="Desarrollo de Software"
      />

      <div className="min-h-screen overflow-x-hidden relative">
        {/* Fondo principal */}
        <div className="fixed inset-0 bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] pointer-events-none"></div>
        
        {/* Sistema de partículas avanzado */}
        <ParticleSystem />
        
        {/* Capa de patrón sutil */}
        <div className="fixed inset-0 opacity-10 pointer-events-none z-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.2) 2px, transparent 2px)',
          backgroundSize: '40px 40px'
        }}></div>
        
        {/* Indicador de progreso de scroll */}
        <motion.div 
          className="fixed right-8 top-1/2 transform -translate-y-1/2 z-50 hidden lg:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <div className="flex flex-col items-center">
            <div className="h-64 w-1 bg-white/20 rounded-full overflow-hidden">
              <motion.div 
                className="w-full bg-gradient-to-b from-[#4ADE80] to-[#3BC571]"
                style={{ height: `${scrollProgress}%` }}
                transition={{ type: "spring", stiffness: 100 }}
              />
            </div>
            <motion.span 
              className="mt-4 text-white/70 text-sm font-medium"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              {Math.round(scrollProgress)}%
            </motion.span>
          </div>
        </motion.div>
        
        {/* Splash Screen */}
        {isLoading && <SplashScreen />}

        {/* ================================================ */}
        {/* HEADER REUTILIZABLE - NUEVO */}
        {/* ================================================ */}
        <Header isSplashLoading={isLoading} />

        {/* ================================================ */}
        {/* HERO SECTION OPTIMIZADO CON KEYWORDS */}
        {/* ================================================ */}
        <ParallaxSection speed={0.3}>
          <section className="relative overflow-hidden pt-24 min-h-[85vh] sm:min-h-[80vh]">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0E2544]/80 via-[#0E2544]/60 to-transparent z-0"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0E2544]/30 to-[#0E2544]/50 z-0"></div>
            
            <div className="relative z-20 max-w-7xl mx-auto">
              <div className="relative z-10 pb-20 lg:pb-28">
                <div className="pt-10 sm:pt-16 lg:pt-20">
                  <div className="mt-10 sm:mt-16 lg:mt-20 mx-auto max-w-4xl lg:max-w-5xl lg:mx-0 lg:pt-10 lg:text-left px-4 sm:px-6 lg:px-8">
                    <motion.div 
                      className="relative"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <motion.div 
                        className="block"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight drop-shadow-lg">
                          Potenciando el éxito
                        </h1>
                      </motion.div>
                      
                      <motion.div 
                        className="block mt-4 md:mt-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ 
                          opacity: 1, 
                          x: 0,
                        }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight drop-shadow-lg">
                          con{' '}
                          <span className="relative inline-block">
                            <LiquidText>soluciones digitales</LiquidText>
                            <motion.div
                              className="absolute inset-0"
                              style={{
                                background: "linear-gradient(90deg, #4ADE80, #3BC571, #2D4A7D, #4ADE80)",
                                backgroundSize: "300% 300%",
                                filter: "blur(8px)",
                                opacity: 0.3,
                                WebkitBackgroundClip: "text",
                                backgroundClip: "text",
                              }}
                              animate={{
                                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                              }}
                              transition={{
                                duration: 5,
                                repeat: Infinity,
                                ease: "linear",
                                delay: 0.5
                              }}
                            />
                          </span>
                        </h1>
                      </motion.div>
                      
                      <motion.div
                        className="absolute -bottom-2 left-0 w-0 h-0.5 bg-gradient-to-r from-[#4ADE80] to-transparent"
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 1, delay: 1.5 }}
                      />
                    </motion.div>
                    
                    {/* TEXTO OPTIMIZADO CON KEYWORDS PRINCIPALES */}
<motion.p 
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ duration: 0.8, delay: 0.6 }}
  className="mt-8 sm:mt-10 text-lg sm:text-xl lg:text-2xl text-white/90 max-w-3xl backdrop-blur-sm bg-white/5 p-6 rounded-xl leading-relaxed"
>
  <strong>Desarrollamos software personalizado que transforma procesos, optimiza recursos y acelera el crecimiento de tu empresa.</strong>
</motion.p>
                    
                    <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <motion.button
                        whileHover={{ 
                          scale: 1.05,
                          boxShadow: "0 5px 20px rgba(74, 222, 128, 0.5)"
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          // Disparar el modal de reunión desde el header
                          const event = new CustomEvent('openMeetingModal');
                          window.dispatchEvent(event);
                        }}
                        className="px-8 py-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2 hover:from-[#3BC571] hover:to-[#2AAE60] relative overflow-hidden text-lg"
                      >
                        <RocketLaunchIcon className="h-6 w-6" />
                        Solicitar Reunión Inicial
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                      </motion.button>
                      <motion.button
                        whileHover={{ 
                          scale: 1.05,
                          boxShadow: "0 5px 20px rgba(255, 255, 255, 0.3)"
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/proyectos')}
                        className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/30 hover:bg-white/30 relative overflow-hidden text-lg"
                      >
                        Ver Todos los Proyectos
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                      </motion.button>
                    </div>
                    
                    {/* KEYWORDS TAGS - AHORA INVISIBLES PERO PRESENTES PARA SEO */}
                    <div className="opacity-0 h-0 overflow-hidden absolute">
                      {keywordStrategy.primary.slice(0, 8).map((kw) => (
                        <span key={kw.keyword}>
                          #{kw.keyword.replace(/\s+/g, '')}
                        </span>
                      ))}
                    </div>

                    {/* Texto de especializaciones visible y elegante */}
                    <div className="mt-8 text-white/60 text-sm max-w-2xl">
                      <span className="font-semibold text-[#4ADE80]">Especializados en:</span>{' '}
                      desarrollo software Córdoba, sistemas gestión empresarial, app mantenimiento industrial, software seguridad laboral
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </ParallaxSection>

        {/* ================================================ */}
        {/* KEYWORDS SECTION - AHORA INVISIBLE PERO SEMÁNTICO */}
        {/* ================================================ */}
        <section className="opacity-0 h-0 overflow-hidden absolute">
          <div className="absolute inset-0 bg-gradient-to-r from-[#4ADE80]/5 via-transparent to-[#3BC571]/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-[#0E2544]/90 to-[#1c3558]/90 backdrop-blur-sm rounded-2xl p-8 border border-[#4ADE80]/20 shadow-2xl">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#4ADE80]/20 flex items-center justify-center">
                    <span className="text-[#4ADE80] text-2xl font-bold">✓</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">
                      Expertos en soluciones digitales para empresas
                    </h3>
                    <p className="text-white/60 text-sm mt-1">
                      Especializados en desarrollo software, gestión empresarial y seguridad industrial
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="px-4 py-2.5 bg-[#4ADE80]/10 text-white rounded-xl text-sm font-medium border border-[#4ADE80]/30">
                    #desarrolloSoftwareCórdoba
                  </span>
                  <span className="px-4 py-2.5 bg-[#4ADE80]/10 text-white rounded-xl text-sm font-medium border border-[#4ADE80]/30">
                    #sistemasGestionEmpresarial
                  </span>
                  <span className="px-4 py-2.5 bg-[#4ADE80]/10 text-white rounded-xl text-sm font-medium border border-[#4ADE80]/30">
                    #appMantenimientoIndustrial
                  </span>
                  <span className="px-4 py-2.5 bg-[#4ADE80]/10 text-white rounded-xl text-sm font-medium border border-[#4ADE80]/30">
                    #softwareSeguridadLaboral
                  </span>
                  <span className="px-4 py-2.5 bg-[#4ADE80]/10 text-white rounded-xl text-sm font-medium border border-[#4ADE80]/30">
                    #PWAArgentina
                  </span>
                </div>
              </div>
              
              {/* Texto oculto semántico para Google */}
              <div className="sr-only">
                Algorithmia es líder en desarrollo software.
              </div>
            </div>
          </div>
        </section>

        {/* ================================================ */}
        {/* KEYWORDS GRID - AHORA INVISIBLE */}
        {/* ================================================ */}
        <section className="opacity-0 h-0 overflow-hidden absolute">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-[#4ADE80]/30 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CodeBracketIcon className="w-4 h-4 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-white font-semibold">Desarrollo Software</h3>
                </div>
                <p className="text-white/60 text-sm">
                  <span className="text-[#4ADE80]">▹</span> Córdoba, Argentina
                </p>
                <p className="text-white/40 text-xs mt-2">
                  #desarrolloSoftwareCórdoba #programaciónReact #sistemasWeb
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-[#4ADE80]/30 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ChartBarIcon className="w-4 h-4 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-white font-semibold">Gestión Empresarial</h3>
                </div>
                <p className="text-white/60 text-sm">
                  <span className="text-[#4ADE80]">▹</span> ERP, BI, RRHH
                </p>
                <p className="text-white/40 text-xs mt-2">
                  #sistemasGestionEmpresarial #ERPArgentina #softwareAdministración
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-[#4ADE80]/30 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <WrenchScrewdriverIcon className="w-4 h-4 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-white font-semibold">Mantenimiento Industrial</h3>
                </div>
                <p className="text-white/60 text-sm">
                  <span className="text-[#4ADE80]">▹</span> PWA, Inspecciones
                </p>
                <p className="text-white/40 text-xs mt-2">
                  #appMantenimientoIndustrial #PWAInspecciones #checklistDigital
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-[#4ADE80]/30 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShieldCheckIcon className="w-4 h-4 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-white font-semibold">Seguridad Laboral</h3>
                </div>
                <p className="text-white/60 text-sm">
                  <span className="text-[#4ADE80]">▹</span> Riesgos, Accidentes
                </p>
                <p className="text-white/40 text-xs mt-2">
                  #softwareSeguridadLaboral #gestiónRiesgos #higieneYSeguridad
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================ */}
        {/* INTRODUCCIÓN */}
        {/* ================================================ */}
        <MorphingSection id="introduccion">
          <section className="py-20 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 z-0"></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true, margin: "-100px" }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="inline-block mb-8"
                >
                  <div className="w-20 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full mx-auto mb-6"></div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                    Transformación Digital a Tu Medida
                  </h2>
                </motion.div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-12 shadow-xl border border-white/20 max-w-6xl mx-auto relative overflow-hidden">
                  <div className="relative z-10">
                    <p className="text-lg text-white/90 mb-6 leading-relaxed">
                      En un mundo cada vez más digital, las empresas necesitan <strong>soluciones tecnológicas ágiles e innovadoras</strong> alineadas con sus objetivos estratégicos. 
                      Somos tu socio confiable en el <strong>desarrollo de software personalizado en Córdoba</strong>, 
                      <strong> sistemas de gestión empresarial para Argentina</strong>, 
                      <strong> aplicaciones PWA para mantenimiento industrial</strong> y 
                      <strong> software de seguridad laboral</strong>.
                    </p>
                    <p className="text-lg text-white/90 leading-relaxed">
                      Con sede en <strong>Córdoba, Argentina</strong>, ayudamos a empresas a transformar sus ideas en realidad, superando desafíos tecnológicos con estrategias inteligentes y herramientas innovadoras.
                    </p>
                  </div>
                  <motion.div 
                    className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571]"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ duration: 1 }}
                    viewport={{ once: true }}
                  />
                </div>
              </motion.div>
            </div>
          </section>
        </MorphingSection>

        {/* ================================================ */}
        {/* SERVICIOS CLAVE INTERACTIVOS */}
        {/* ================================================ */}
        <MorphingSection id="servicios" className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center mb-16"
            >
              <div className="inline-block">
                <div className="w-20 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full mx-auto mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  Nuestros Servicios Clave
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto">
                  Soluciones completas para tu <strong>transformación digital</strong>
                </p>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative group"
                whileHover={{ y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4ADE80]/20 to-[#3BC571]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10 bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-white/20 group-hover:border-[#4ADE80]/50 transition-all duration-300 h-full">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-xl flex items-center justify-center mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CodeBracketIcon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-4">Desarrollo de Software a Medida</h3>
                  <p className="text-white/80 mb-6">
                    Creemos firmemente que no existe una solución única para todos. Cada organización tiene necesidades específicas y desafíos únicos.
                  </p>
                  <motion.ul 
                    className="space-y-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.1 }}
                  >
                    {["Sistemas de gestión empresarial", "Aplicaciones web y móviles PWA", "Integraciones tecnológicas"].map((item, idx) => (
                      <motion.li 
                        key={idx}
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-center"
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-[#4ADE80]/20 rounded-full flex items-center justify-center mr-3">
                          <CheckCircleIcon className="h-4 w-4 text-[#4ADE80]" />
                        </div>
                        <span className="text-white/90">{item}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                  {/* Keywords ocultas para SEO */}
                  <div className="sr-only">
                    Keywords: desarrollo software Córdoba, sistemas gestión empresarial Argentina, 
                    aplicaciones PWA, desarrollo React Argentina
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="relative group"
                whileHover={{ y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4ADE80]/20 to-[#3BC571]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10 bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-white/20 group-hover:border-[#4ADE80]/50 transition-all duration-300 h-full">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-xl flex items-center justify-center mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChartBarIcon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-4">Consultoría Tecnológica</h3>
                  <p className="text-white/80 mb-6">
                    Adoptar la tecnología correcta puede marcar la diferencia entre el crecimiento y la obsolescencia.
                  </p>
                  <motion.ul 
                    className="space-y-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.1 }}
                  >
                    {["Análisis de sistemas actuales", "Estrategias tecnológicas personalizadas", "Asesoramiento en implementación"].map((item, idx) => (
                      <motion.li 
                        key={idx}
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-center"
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-[#4ADE80]/20 rounded-full flex items-center justify-center mr-3">
                          <CheckCircleIcon className="h-4 w-4 text-[#4ADE80]" />
                        </div>
                        <span className="text-white/90">{item}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                  <div className="sr-only">
                    Keywords: consultoría tecnológica Córdoba, transformación digital pymes, 
                    asesoramiento tecnológico Argentina
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="relative group"
                whileHover={{ y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4ADE80]/20 to-[#3BC571]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10 bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-white/20 group-hover:border-[#4ADE80]/50 transition-all duration-300 h-full">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-xl flex items-center justify-center mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShieldCheckIcon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-4">Testing y QA</h3>
                  <p className="text-white/80 mb-6">
                    Aseguramos que tus aplicaciones sean confiables, seguras y de alta calidad antes de llegar al mercado.
                  </p>
                  <motion.ul 
                    className="space-y-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.1 }}
                  >
                    {["Pruebas funcionales y de rendimiento", "Automatización de pruebas", "Pruebas de seguridad"].map((item, idx) => (
                      <motion.li 
                        key={idx}
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-center"
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-[#4ADE80]/20 rounded-full flex items-center justify-center mr-3">
                          <CheckCircleIcon className="h-4 w-4 text-[#4ADE80]" />
                        </div>
                        <span className="text-white/90">{item}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                </div>
              </motion.div>
            </div>
          </div>
        </MorphingSection>

        {/* ================================================ */}
        {/* GALERÍA CINÉTICA */}
        {/* ================================================ */}
        <MorphingSection id="galeria" className="py-20 relative">
          <KineticGallery images={galleryImages} />
        </MorphingSection>

        {/* ================================================ */}
        {/* PORTAFOLIO DE PROYECTOS INTERACTIVOS */}
        {/* ================================================ */}
        <MorphingSection id="proyectos" className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center mb-16"
            >
              <div className="inline-block">
                <div className="w-20 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full mx-auto mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  Portafolio de Proyectos
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto">
                  <strong>Soluciones especializadas</strong> que hemos desarrollado para empresas argentinas
                </p>
              </div>
            </motion.div>

            <motion.div 
              variants={container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {services.slice(0, 6).map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div 
                    key={index} 
                    variants={item}
                    className="relative group"
                    whileHover={{ y: -10 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4ADE80]/20 to-[#3BC571]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <motion.div 
                      className="relative z-10 bg-white/10 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/20 group-hover:border-[#4ADE80]/50 transition-all duration-300 h-full"
                      style={{ 
                        transformStyle: "preserve-3d",
                        perspective: "1000px"
                      }}
                      whileHover={{ 
                        rotateY: 5,
                        rotateX: 5,
                        scale: 1.02
                      }}
                    >
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        <div className="flex items-start mb-4 relative z-10">
                          <motion.div 
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                            className="p-3 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-lg shadow-md"
                          >
                            <Icon className="h-6 w-6 text-white" />
                          </motion.div>
                          <h3 className="ml-3 text-lg font-bold text-white group-hover:text-[#4ADE80] transition-colors">
                            {service.title}
                          </h3>
                        </div>
                        <p className="text-white/80 mb-4 relative z-10">{service.description}</p>
                        
                        <motion.div 
                          className="overflow-hidden"
                          initial={{ height: 0 }}
                          whileHover={{ height: "auto" }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
                            {service.features.map((feature, idx) => (
                              <motion.span 
                                key={idx}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3, delay: idx * 0.1 }}
                                viewport={{ once: true }}
                                className="px-3 py-1.5 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors border border-white/20 hover:border-[#4ADE80]/50 cursor-pointer"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                {feature}
                              </motion.span>
                            ))}
                          </div>
                        </motion.div>
                        
                        {/* Keywords específicas del servicio - Ocultas para Google */}
                        <div className="sr-only">
                          Keywords específicas: {service.keywords?.keywords?.join(', ')}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center mt-16"
            >
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/proyectos')}
                className="px-8 py-3.5 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:from-[#3BC571] hover:to-[#2AAE60] border border-white/20 relative overflow-hidden"
              >
                <span className="relative z-10">Ver Todos los Proyectos</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
              </motion.button>
            </motion.div>
          </div>
        </MorphingSection>

        {/* ================================================ */}
        {/* TECNOLOGÍAS INTERACTIVAS */}
        {/* ================================================ */}
        <MorphingSection id="tecnologia" className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center mb-16"
            >
              <div className="inline-block">
                <div className="w-20 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full mx-auto mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  Tecnologías que Utilizamos
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto">
                  Stack moderno para <strong>soluciones robustas y escalables</strong>
                </p>
              </div>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6 mb-16">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8, rotate: -180 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.2,
                    rotate: 360,
                    transition: { duration: 0.3 }
                  }}
                  className="relative group"
                >
                  <motion.div 
                    className={`px-8 py-4 rounded-xl text-white font-semibold shadow-2xl bg-gradient-to-r ${tech.color} relative overflow-hidden cursor-pointer`}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="relative z-10">{tech.name}</span>
                    
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.6 }}
                    />
                  </motion.div>
                  
                  <motion.div 
                    className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none"
                    initial={{ y: 10, opacity: 0 }}
                    whileHover={{ y: 0, opacity: 1 }}
                  >
                    {tech.name} en nuestro stack
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <div className="text-center bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-400 border border-white/20 hover:border-[#4ADE80]/50 relative overflow-hidden group">
                <div className="relative z-10">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CloudArrowUpIcon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-3">Arquitectura Cloud</h3>
                  <p className="text-white/80">Soluciones escalables en la nube con alta disponibilidad</p>
                </div>
              </div>

              <div className="text-center bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-400 border border-white/20 hover:border-[#4ADE80]/50 relative overflow-hidden group">
                <div className="relative z-10">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <DeviceTabletIcon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-3">PWA Multiplataforma</h3>
                  <p className="text-white/80">Una aplicación, múltiples dispositivos sin instalación</p>
                  <div className="sr-only">
                    Progressive Web Apps, desarrollo PWA Argentina, aplicaciones offline
                  </div>
                </div>
              </div>

              <div className="text-center bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-400 border border-white/20 hover:border-[#4ADE80]/50 relative overflow-hidden group">
                <div className="relative z-10">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LockClosedIcon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-3">Seguridad Avanzada</h3>
                  <p className="text-white/80">Protección de datos y cumplimiento normativo</p>
                </div>
              </div>
            </motion.div>
          </div>
        </MorphingSection>

        {/* ================================================ */}
        {/* PROCESO DE TRABAJO */}
        {/* ================================================ */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center mb-16"
            >
              <div className="inline-block">
                <div className="w-20 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full mx-auto mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  Nuestro Proceso de Trabajo
                </h2>
                <p className="text-lg text-white/80 max-w-2xl mx-auto">
                  Metodología ágil y transparente para <strong>resultados óptimos</strong>
                </p>
              </div>
            </motion.div>

            <div className="relative">
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-[#4ADE80] via-[#3BC571] to-[#4ADE80] rounded-full opacity-30"></div>
              
              <div className="flex flex-col gap-12">
                {workProcess.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 text-right' : 'md:pl-12'}`}>
                      <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl inline-block border border-white/20 hover:border-[#4ADE80]/50 transition-all duration-300 max-w-md relative overflow-hidden group">
                        <div className="relative z-10">
                          <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                          <p className="text-white/80">{step.description}</p>
                        </div>
                        <motion.div 
                          className="absolute inset-0 bg-gradient-to-r from-[#4ADE80]/10 to-[#3BC571]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "100%" }}
                          transition={{ duration: 0.6 }}
                        />
                      </div>
                    </div>
                    
                    <div className="hidden md:flex items-center justify-center w-20 h-20 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full text-white text-2xl font-bold mx-4 z-10 border-4 border-white/30 shadow-lg">
                      {step.step}
                    </div>
                    
                    <div className="hidden md:block w-1/2"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================================================ */}
        {/* BENEFICIOS */}
        {/* ================================================ */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              className="text-center mb-16"
            >
              <div className="inline-block">
                <div className="w-20 h-1 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-full mx-auto mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Beneficios de Trabajar Con Nosotros
                </h2>
                <p className="text-lg text-white/80 max-w-3xl mx-auto">
                  Descubre por qué somos el <strong>socio ideal para tu transformación digital</strong>
                </p>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
              {[
                { 
                  icon: CheckBadgeIcon, 
                  title: "Calidad Garantizada", 
                  desc: "Estándares de desarrollo de primer nivel",
                  color: "from-[#0E2544] to-[#2D4A7D]"
                },
                { 
                  icon: ClockIcon, 
                  title: "Entregas a Tiempo", 
                  desc: "Metodologías ágiles para cumplir plazos",
                  color: "from-[#4ADE80] to-[#3BC571]"
                },
                { 
                  icon: UsersIcon, 
                  title: "Equipo Especializado", 
                  desc: "Profesionales con amplia experiencia",
                  color: "from-[#0E2544] to-[#4ADE80]"
                },
                { 
                  icon: ArrowTrendingUpIcon, 
                  title: "ROI Medible", 
                  desc: "Resultados tangibles para tu inversión",
                  color: "from-[#4ADE80] to-[#3BC571]"
                },
                { 
                  icon: ShieldCheckIcon, 
                  title: "Seguridad", 
                  desc: "Protección de datos y cumplimiento",
                  color: "from-[#0E2544] via-[#2D4A7D] to-[#4ADE80]"
                },
                { 
                  icon: CloudIcon, 
                  title: "Escalabilidad", 
                  desc: "Crecimiento con tu negocio",
                  color: "from-[#2D4A7D] to-[#4ADE80]"
                },
                { 
                  icon: CursorArrowRaysIcon, 
                  title: "Soporte Continuo", 
                  desc: "Acompañamiento post-implementación",
                  color: "from-[#4ADE80] to-[#3BC571]"
                },
                { 
                  icon: LightBulbIcon, 
                  title: "Innovación", 
                  desc: "Tecnologías de vanguardia",
                  color: "from-[#0E2544] to-[#4ADE80]"
                }
              ].map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`rounded-2xl p-8 transition-all duration-400 relative overflow-hidden h-full min-h-[240px] flex flex-col border border-white/20 shadow-xl hover:shadow-2xl hover:-translate-y-2 hover:scale-102 bg-gradient-to-br ${benefit.color} group`}
                  >
                    <div className="relative z-10">
                      <motion.div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 backdrop-blur-sm bg-white/20"
                        whileHover={{ scale: 1.1, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon className="w-10 h-10 text-white drop-shadow-md" />
                      </motion.div>
                      <h3 className="text-2xl font-bold mb-3 relative z-10 text-white">
                        {benefit.title}
                      </h3>
                      <p className="text-white/95 text-base mt-auto relative z-10">
                        {benefit.desc}
                      </p>
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================================================ */}
        {/* CTA FINAL */}
        {/* ================================================ */}
        <MorphingSection id="contacto" className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0E2544]/90 via-[#1c3558]/90 to-[#2D4A7D]/90">
            <div className="absolute inset-0 bg-gradient-to-br from-[#4ADE80]/10 via-transparent to-[#3BC571]/10"></div>
          </div>
          
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white pointer-events-none"
              style={{
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, (Math.random() - 0.5) * 40],
                x: [0, (Math.random() - 0.5) * 30],
                opacity: [0, 0.6 * Math.random(), 0],
                scale: [1, 1.3, 1]
              }}
              transition={{
                duration: Math.random() * 6 + 6,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: "easeInOut",
                delay: Math.random() * 3
              }}
            />
          ))}
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              className="relative"
            >
              <div className="inline-block mb-8">
                <div className="w-20 h-1 bg-white/70 rounded-full mx-auto mb-6"></div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
                  ¿Listo para Transformar tu Empresa?
                </h2>
              </div>
              
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-8 md:p-12 shadow-2xl border border-white/30 max-w-4xl mx-auto relative overflow-hidden">
                <div className="relative z-10">
                  <p className="text-xl text-white/95 mb-8 leading-relaxed">
                    Estamos entusiasmados por la oportunidad de colaborar con tu empresa y contribuir al cumplimiento de tus <strong>objetivos tecnológicos y comerciales</strong>.
                  </p>
                  
                  <div className="flex flex-col gap-8">
                    <motion.button
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 5px 20px rgba(74, 222, 128, 0.5)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const event = new CustomEvent('openMeetingModal');
                        window.dispatchEvent(event);
                      }}
                      className="px-8 py-4 bg-white text-[#0E2544] font-bold rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 border-2 border-white hover:bg-transparent hover:text-white flex items-center justify-center gap-3 max-w-xs mx-auto relative overflow-hidden"
                    >
                      <RocketLaunchIcon className="h-6 w-6" />
                      <span className="relative z-10">Solicitar Reunión Inicial</span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                    </motion.button>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <motion.div 
                        className="p-6 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 relative overflow-hidden group"
                        whileHover={{ y: -5 }}
                      >
                        <div className="relative z-10">
                          <h4 className="font-bold text-[#4ADE80] mb-2">Email</h4>
                          <p className="text-white/95">contacto@algorithmia.com</p>
                        </div>
                      </motion.div>
                      <motion.div 
                        className="p-6 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 relative overflow-hidden group"
                        whileHover={{ y: -5 }}
                      >
                        <div className="relative z-10">
                          <h4 className="font-bold text-[#4ADE80] mb-2">Teléfonos</h4>
                          <p className="text-white/95">+54 (351) 2489015 +54 (351) 2021393</p>
                        </div>
                      </motion.div>
                      <motion.div 
                        className="p-6 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 relative overflow-hidden group"
                        whileHover={{ y: -5 }}
                      >
                        <div className="relative z-10">
                          <h4 className="font-bold text-[#4ADE80] mb-2">Ubicación</h4>
                          <p className="text-white/95">Córdoba, Argentina</p>
                        </div>
                      </motion.div>
                    </div>
                    
                    <motion.p 
                      className="text-white/90 text-sm mt-8 italic backdrop-blur-sm bg-white/10 p-4 rounded-xl"
                      animate={{ 
                        scale: [1, 1.02, 1],
                        opacity: [0.9, 1, 0.9]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatType: 'reverse'
                      }}
                    >
                      Juntos, podemos construir <strong>soluciones tecnológicas</strong> que transformen tu negocio. El futuro de tu empresa comienza hoy.
                    </motion.p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </MorphingSection>

        {/* ================================================ */}
        {/* MODALES - AHORA MANEJADOS POR EL HEADER */}
        {/* ================================================ */}

        {/* ================================================ */}
        {/* FOOTER CON KEYWORDS - ACTUALIZADO CON LINK */}
        {/* ================================================ */}
        <footer className="py-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0E2544]/90 via-[#1c3558]/90 to-[#2D4A7D]/90"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="flex items-center">
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="h-12 w-12 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-xl flex items-center justify-center shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <OptimizedImage 
                      src={LogoAlgorithmia} 
                      alt="Algorithmia - Desarrollo de Software Córdoba" 
                      className="w-8 h-8 object-contain"
                    />
                  </motion.div>
                  <div>
                    <span className="text-white font-bold text-xl">Algorithmia</span>
                    <p className="text-white/70 text-sm">Intelligent Logic. Digital Solutions.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-6">
                {/* CAMBIOS: href a to */}
                <Link 
                  to="/servicios" 
                  className="text-white/80 hover:text-[#4ADE80] transition-colors duration-200 relative py-1"
                >
                  <span className="relative">
                    Servicios
                    <motion.div 
                      className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#4ADE80]"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </span>
                </Link>
                <Link 
                  to="/proyectos" 
                  className="text-white/80 hover:text-[#4ADE80] transition-colors duration-200 relative py-1"
                >
                  <span className="relative">
                    Proyectos
                    <motion.div 
                      className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#4ADE80]"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </span>
                </Link>
                <Link 
                  to="/tecnologia" 
                  className="text-white/80 hover:text-[#4ADE80] transition-colors duration-200 relative py-1"
                >
                  <span className="relative">
                    Tecnología
                    <motion.div 
                      className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#4ADE80]"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </span>
                </Link>
                <Link 
                  to="/contacto" 
                  className="text-white/80 hover:text-[#4ADE80] transition-colors duration-200 relative py-1"
                >
                  <span className="relative">
                    Contacto
                    <motion.div 
                      className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#4ADE80]"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </span>
                </Link>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-white/60 text-sm">© {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.</p>
                <p className="text-white/40 text-xs mt-1">Transformando ideas en soluciones digitales.</p>
              </div>
            </div>
            
            <motion.div 
              className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent my-8"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            />
            
            <div className="text-center">
              <p className="text-white/50 text-sm mb-4">
                <strong>Córdoba, Argentina</strong> • contacto@algorithmia.com • +54 (351) 2489015 - +54 (9 351) 2021393
              </p>
              
              {/* KEYWORDS EN FOOTER - AHORA INVISIBLES */}
              <div className="opacity-0 h-0 overflow-hidden absolute">
                <span>desarrollo software Córdoba</span>
                <span>•</span>
                <span>sistemas gestión empresarial Argentina</span>
                <span>•</span>
                <span>app mantenimiento industrial</span>
                <span>•</span>
                <span>software seguridad laboral</span>
                <span>•</span>
                <span>PWA Argentina</span>
                <span>•</span>
                <span>consultoría tecnológica Córdoba</span>
                <span>•</span>
                <span>desarrollo React Argentina</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AboutPage;